import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../I18nContext";
import { api, formatApiErrorDetail } from "../api";

export default function Feedback() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [bug, setBug] = useState("");
  const [feature, setFeature] = useState("");
  const [comment, setComment] = useState("");
  const [images, setImages] = useState([]);
  const [recording, setRecording] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  const readFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  const onImagePick = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    if (images.length + picked.length > 2) { setErr(t("feedback.tooManyImages")); return; }
    const next = [...images];
    for (const file of picked) {
      if (!file.type.startsWith("image/")) { setErr(t("feedback.imageOnly")); return; }
      if (file.size > 3_000_000) { setErr(t("feedback.imageTooBig")); return; }
      next.push({ name: file.name, data: await readFile(file) });
    }
    setErr("");
    setImages(next);
  };

  const onRecordingPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) { setErr(t("feedback.videoOnly")); return; }
    if (file.size > 8_000_000) { setErr(t("feedback.videoTooBig")); return; }
    setErr("");
    setRecording({ name: file.name, data: await readFile(file) });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/feedback", {
        rating: rating || null,
        bug,
        feature,
        comment,
        images: images.map((item) => item.data),
        recording: recording?.data || "",
      });
      setSent(true);
      setTimeout(() => navigate("/app"), 2200);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="tb-page" data-testid="feedback-page">
        <div className="tb-block tb-feedback-sent-page" data-testid="feedback-sent">
          <div style={{ fontSize: "3rem", color: "var(--accent)" }}>✓</div>
          <h1 className="tb-h1" style={{ marginTop: 8 }}>{t("feedback.thanks")}</h1>
          <p className="tb-muted" style={{ maxWidth: 480, margin: "8px auto 0" }}>{t("feedback.thanksBody")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tb-page tb-feedback-page" data-testid="feedback-page">
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <h1 className="tb-h1" data-testid="feedback-heading">{t("feedback.title")}</h1>
        <p className="tb-muted" style={{ marginTop: 4, marginBottom: 20 }}>{t("feedback.intro")}</p>

        <form onSubmit={submit} className="tb-form tb-feedback-form">
          <div className="tb-block">
            <label style={{ display: "block", marginBottom: 12, textAlign: "center" }}><strong>{t("feedback.ratingLabel")}</strong></label>
            <div className="tb-feedback-stars" data-testid="feedback-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`tb-feedback-star ${(hover || rating) >= n ? "on" : ""}`}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(n)}
                  aria-label={`${n} stars`}
                  data-testid={`feedback-star-${n}`}
                >
                  <svg width="34" height="34" viewBox="0 0 24 24" fill={(hover || rating) >= n ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                </button>
              ))}
            </div>
            <div className="tb-muted" style={{ fontSize: "0.85rem", textAlign: "center", marginTop: 8 }}>
              {rating ? t(`feedback.rating${rating}`) : t("feedback.tapStar")}
            </div>
          </div>

          <div className="tb-block">
            <label className="tb-feedback-row-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span aria-hidden="true">🐞</span>
              <strong>{t("feedback.bugLabel")}</strong>
            </label>
            <textarea
              value={bug}
              onChange={(e) => setBug(e.target.value)}
              placeholder={t("feedback.bugPlaceholder")}
              style={{ minHeight: 100 }}
              data-testid="feedback-bug"
            />
          </div>

          <div className="tb-block">
            <label className="tb-feedback-row-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span aria-hidden="true">💡</span>
              <strong>{t("feedback.featureLabel")}</strong>
            </label>
            <textarea
              value={feature}
              onChange={(e) => setFeature(e.target.value)}
              placeholder={t("feedback.featurePlaceholder")}
              style={{ minHeight: 100 }}
              data-testid="feedback-feature"
            />
          </div>

          <div className="tb-block">
            <label className="tb-feedback-row-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span aria-hidden="true">💖</span>
              <strong>{t("feedback.anythingLabel")}</strong>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("feedback.anythingPlaceholder")}
              style={{ minHeight: 100 }}
              data-testid="feedback-comment"
            />
          </div>

          <div className="tb-block">
            <label className="tb-feedback-row-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span aria-hidden="true">📸</span>
              <strong>{t("feedback.imageLabel")}</strong>
            </label>
            <p className="tb-muted" style={{ margin: "4px 0 10px", fontSize: "0.82rem" }}>{t("feedback.imageHelp")}</p>
            {images.map((item, index) => (
              <div className="tb-feedback-image-preview" data-testid="feedback-image-preview" key={`${item.name}-${index}`} style={{ marginBottom: 8 }}>
                <img src={item.data} alt={item.name} />
                <div className="tb-feedback-image-meta">
                  <span className="tb-muted" style={{ fontSize: "0.8rem" }}>{item.name}</span>
                  <button type="button" className="tb-ghost-btn" onClick={() => setImages((prev) => prev.filter((_, i) => i !== index))} data-testid="feedback-image-remove" style={{ fontSize: "0.78rem", padding: "4px 10px" }}>
                    {t("feedback.removeImage")}
                  </button>
                </div>
              </div>
            ))}
            {images.length < 2 && (
              <label className="tb-feedback-image-pick" data-testid="feedback-image-pick">
                <input type="file" accept="image/*" multiple onChange={onImagePick} style={{ display: "none" }} data-testid="feedback-image-input" />
                <span>📎 {t("feedback.attachImage")}</span>
              </label>
            )}
            <p className="tb-muted" style={{ margin: "14px 0 10px", fontSize: "0.82rem" }}>{t("feedback.videoHelp")}</p>
            {recording ? (
              <div className="tb-feedback-image-meta" data-testid="feedback-recording-name">
                <span className="tb-muted" style={{ fontSize: "0.8rem" }}>{recording.name}</span>
                <button type="button" className="tb-ghost-btn" onClick={() => setRecording(null)} data-testid="feedback-recording-remove" style={{ fontSize: "0.78rem", padding: "4px 10px" }}>
                  {t("feedback.removeImage")}
                </button>
              </div>
            ) : (
              <label className="tb-feedback-image-pick" data-testid="feedback-recording-pick">
                <input type="file" accept="video/*" onChange={onRecordingPick} style={{ display: "none" }} data-testid="feedback-recording-input" />
                <span>📎 {t("feedback.attachVideo")}</span>
              </label>
            )}
          </div>

          {err && <div className="tb-form-error" data-testid="feedback-error">{err}</div>}

          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap" }}>
            <button type="button" className="tb-ghost-btn" onClick={() => navigate(-1)} data-testid="feedback-cancel">
              {t("feedback.cancel")}
            </button>
            <button type="submit" className="tb-primary-btn" disabled={busy} data-testid="feedback-submit">
              {busy ? "…" : `▸ ${t("feedback.submit")}`}
            </button>
          </div>
          <p className="tb-muted" style={{ fontSize: "0.8rem", textAlign: "center", margin: 0 }}>
            {t("feedback.footnote")}
          </p>
        </form>
      </div>
    </div>
  );
}
